import {
  DEFAULT_LOCALE,
  isLocale,
  LOCALES,
  localizePath,
  splitLocale,
  stripLocale,
  type Locale,
} from "@lib/i18n/locale";
// eslint-disable-next-line no-restricted-imports -- the one place that wraps it
import { type NextRouter, useRouter as useNextRouter } from "next/router";
import { interpolateAs } from "next/dist/shared/lib/router/utils/interpolate-as";
import { getRouteMatcher } from "next/dist/shared/lib/router/utils/route-matcher";
import { getRouteRegex } from "next/dist/shared/lib/router/utils/route-regex";
import type { ParsedUrlQuery, ParsedUrlQueryInput } from "querystring";
import { useMemo } from "react";
import type { UrlObject } from "url";

// The Pages Router as it behaved while `i18n` was set in next.config.js, now that the locale is
// the `[locale]` route segment instead: `asPath`, `pathname` and `query` leave the locale out,
// `locale` names it, and navigation adds its prefix to the German, unprefixed paths that the
// path builders return.

type Url = string | UrlObject;

interface TransitionOptions {
  locale?: string | false;
  shallow?: boolean;
  scroll?: boolean;
}

const LOCALE_SEGMENT = "/[locale]";

// Mocked routers in tests leave these out.
const asPathOf = (router: NextRouter) => router.asPath ?? "/";
const pathnameOf = (router: NextRouter) => router.pathname ?? "/";

// From the visible path, not from `query.locale`: after hydration and after a shallow update,
// Next matches a German, unprefixed URL against `[locale]/[[...slug]]` on its own and reads
// the first path segment as the locale.
const currentLocale = (router: NextRouter): Locale =>
  splitLocale(asPathOf(router)).locale;

const publicPathname = (router: NextRouter) => {
  const pathname = pathnameOf(router);

  return pathname.startsWith(LOCALE_SEGMENT)
    ? pathname.slice(LOCALE_SEGMENT.length) || "/"
    : pathname;
};

const matchRoute = (pattern: string, pathname: string) =>
  getRouteMatcher(getRouteRegex(pattern))(pathname) || null;

const routeQueries = new WeakMap<
  ParsedUrlQuery,
  { asPath: string; query: ParsedUrlQuery }
>();

/**
 * The query without the locale, and with the route params read from the visible path where it
 * fits the route; see `currentLocale` for what Next puts there otherwise.
 */
const routeQuery = (router: NextRouter) => {
  const raw = router.query ?? {};
  const cached = routeQueries.get(raw);

  if (cached && cached.asPath === asPathOf(router)) {
    return cached.query;
  }

  const { locale: _locale, ...rest } = raw;
  const pathname = stripLocale(asPathOf(router)).split(/[?#]/)[0] ?? "/";
  const query = {
    ...rest,
    ...matchRoute(publicPathname(router), pathname),
  };

  routeQueries.set(raw, { asPath: asPathOf(router), query });

  return query;
};

const toSearch = (query: ParsedUrlQueryInput) => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    for (const item of ([] as unknown[]).concat(value ?? [])) {
      params.append(key, String(item));
    }
  }

  const search = params.toString();

  return search ? `?${search}` : "";
};

/** `/compare/[stepId]` with `{ stepId: "x", utm: "y" }` becomes `/compare/x?utm=y`. */
const interpolate = (pathname: string, query: ParsedUrlQueryInput) => {
  const { params, result } = interpolateAs(
    pathname,
    pathname,
    query as ParsedUrlQuery,
  );
  const rest = Object.fromEntries(
    Object.entries(query).filter(([key]) => !params.includes(key)),
  );

  return `${result}${toSearch(rest)}`;
};

const toPath = (url: Url, router: NextRouter) => {
  if (typeof url === "string") {
    return url;
  }

  const query =
    typeof url.query === "string"
      ? Object.fromEntries(new URLSearchParams(url.query))
      : (url.query ?? {});
  const hash = url.hash ? `#${url.hash.replace(/^#/, "")}` : "";

  return `${interpolate(url.pathname ?? publicPathname(router), query)}${hash}`;
};

const navigate =
  (router: NextRouter, method: "push" | "replace") =>
  (url: Url, as?: Url, options?: TransitionOptions) => {
    const { locale: requested, ...rest } = options ?? {};
    const path = toPath(as ?? url, router);
    const target =
      requested === false
        ? path
        : localizePath(
            isLocale(requested) ? requested : currentLocale(router),
            path,
          );

    return options === undefined
      ? router[method](target)
      : router[method](target, undefined, rest);
  };

export type LocaleRouter = Omit<
  NextRouter,
  "locale" | "locales" | "defaultLocale"
> & {
  locale: Locale;
  locales: Locale[];
  defaultLocale: Locale;
};

export const useRouter = (): LocaleRouter => {
  const router = useNextRouter();

  return useMemo(
    () =>
      new Proxy(router, {
        get(target, property) {
          switch (property) {
            case "locale":
              return currentLocale(target);
            case "locales":
              return [...LOCALES];
            case "defaultLocale":
              return DEFAULT_LOCALE;
            case "asPath":
              return stripLocale(asPathOf(target));
            case "pathname":
              return publicPathname(target);
            case "query":
              return routeQuery(target);
            case "push":
            case "replace":
              return navigate(target, property);
            case "prefetch":
              return (url: string, as?: string, options?: object) =>
                target.prefetch(
                  localizePath(currentLocale(target), url),
                  as && localizePath(currentLocale(target), as),
                  options,
                );
            default: {
              const value = Reflect.get(target, property, target);

              return typeof value === "function" ? value.bind(target) : value;
            }
          }
        },
      }) as unknown as LocaleRouter,
    [router],
  );
};
